import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { merge, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { TipoFirmante } from 'src/app/pages/configuracion/Envios/envios/models/tipo-firmante';
import { FormularioDocumentoService } from 'src/app/pages/configuracion/Envios/documentos/services/formulario-documento.service';
import { FormularioValidacionesDocsService } from 'src/app/pages/configuracion/Envios/documentos/services/formulario-validaciones-docs.service';
import { MODO_ENVIO_SIMPLE } from 'src/app/pages/configuracion/Envios/documentos/services/modo-envio-simple.token';
import { DatosConfiguracionDocumentosService } from 'src/app/pages/configuracion/Envios/documentos/services/datos-configuracion-documentos.service';
import { BreadcrumbsService } from 'src/app/shared/services/breadcrumbs.service';
import { ConfirmationMessageComponent } from 'src/app/shared/components/sqp-components/sqpMensajeConfirmacion/components/confirmation-message/confirmation-message.component';
import { TipoMensajeConfirmacion } from 'src/app/shared/components/sqp-components/sqpMensajeConfirmacion/models/confirmation-message-data';
import { DocumentoEnvioSimple } from '../../models/documento-envio-simple';
import { FirmanteRosterItem } from '../../models/firmante-roster-item';
import { EnvioSimpleFlowService } from '../../services/envio-simple-flow.service';

/** Paso del wizard de configuracion de documento reutilizado en Envio simple (panel unificado). */
const INDEX_PASO_PANEL_UNIFICADO = 8;

@Component({
  selector: 'app-envio-simple-firmantes-page',
  templateUrl: './envio-simple-firmantes-page.component.html',
  styleUrls: ['./envio-simple-firmantes-page.component.css'],
  providers: [
    FormularioDocumentoService,
    // Misma instancia que FormularioDocumentoService: el root no ve los borradores del documento activo.
    FormularioValidacionesDocsService,
    { provide: MODO_ENVIO_SIMPLE, useValue: true }
  ]
})
export class EnvioSimpleFirmantesPageComponent implements OnInit, OnDestroy {
  readonly indexPaso = INDEX_PASO_PANEL_UNIFICADO;
  pdfSrc: string | null = null;
  firmanteSeleccionado: TipoFirmante;
  documentoActivoId: string | null = null;
  puedeContinuar = false;

  private pdfObjectUrl: string | null = null;
  private formularioChangesSub?: Subscription;
  private readonly destroy$ = new Subject<void>();

  constructor(
    public envioSimpleFlowService: EnvioSimpleFlowService,
    public formularioDocumentoService: FormularioDocumentoService,
    private formularioValidacionesDocsService: FormularioValidacionesDocsService,
    private datosConfiguracionDocumentosService: DatosConfiguracionDocumentosService,
    private breadcrumbsService: BreadcrumbsService,
    private router: Router,
    private snackBar: MatSnackBar,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    if (!this.envioSimpleFlowService.tieneDocumentos) {
      this.router.navigate(['/panel/envio-simple/documentos']);
      return;
    }
    this.envioSimpleFlowService.establecerPaso(1);
    this.envioSimpleFlowService.prepararPasoFirmantes();
    this.breadcrumbsService.addBreadcrumbs('Envío simple');

    this.formularioDocumentoService.indexPaso = this.indexPaso;

    this.envioSimpleFlowService.documentoActivoId$
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(id => this.cargarDocumentoActivo(id));

    merge(
      this.envioSimpleFlowService.documentos$,
      this.envioSimpleFlowService.documentoActivoId$,
      this.formularioValidacionesDocsService.validacionFormulariosRevision$
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.actualizarPuedeContinuar());
  }

  ngOnDestroy(): void {
    this.formularioChangesSub?.unsubscribe();
    this.revocarPdfObjectUrl();
    this.destroy$.next();
    this.destroy$.complete();
  }

  volver(): void {
    this.envioSimpleFlowService.establecerPaso(0);
    this.router.navigate(['/panel/envio-simple/documentos']);
  }

  /** Mismo patron que paso Documentos: si Siguiente esta deshabilitado, informar errores. */
  onContinuarClick(): void {
    if (!this.puedeContinuar) {
      this.persistirDocumentoActivoEnStore();
      this.validarParaContinuar();
    }
  }

  siguiente(): void {
    // Persistir el documento activo (FormGroup + borradores) antes de validar
    // para que las reglas de flujo vean su estado mas reciente.
    this.persistirDocumentoActivoEnStore();
    if (!this.validarParaContinuar()) {
      return;
    }
    const firmantesSinConfig = this.envioSimpleFlowService.obtenerFirmantesRosterSinConfiguracion();
    if (firmantesSinConfig.length > 0) {
      this.confirmarEliminarFirmantesSinConfiguracion(firmantesSinConfig);
      return;
    }
    this.avanzarAlPasoEnvio();
  }

  private confirmarEliminarFirmantesSinConfiguracion(firmantes: FirmanteRosterItem[]): void {
    const nombres = firmantes.map(item => item.desc).join(', ');
    const textoEliminar =
      firmantes.length === 1
        ? 'Continuar eliminando firmante'
        : 'Continuar eliminando firmantes';
    const mensaje =
      firmantes.length === 1
        ? `El firmante <strong>${nombres}</strong> no está configurado en ningún documento. Puede continuar eliminándolo o mantenerlo en el envío (por ejemplo, para que solo visualice los documentos).`
        : `Los firmantes <strong>${nombres}</strong> no están configurados en ningún documento. Puede continuar eliminándolos o mantenerlos en el envío (por ejemplo, para que solo visualicen los documentos).`;

    const dialogRef = this.dialog.open(ConfirmationMessageComponent, {
      data: {
        tipo: TipoMensajeConfirmacion.ConfirmacionSimple,
        icono: 'icon-info',
        titulo: 'Firmantes sin configuración',
        mensaje,
        textoConfirmacion: textoEliminar,
        textoBoton: textoEliminar,
        iconoConfirmacion: 'icon-arrow-right',
        extraInfo: { boton: 'Continuar sin eliminar' }
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'OK') {
        this.envioSimpleFlowService.eliminarFirmantesRosterSinConfiguracion();
        this.avanzarAlPasoEnvio();
      } else if (result === 'desactivar') {
        this.avanzarAlPasoEnvio();
      }
    });
  }

  private avanzarAlPasoEnvio(): void {
    this.envioSimpleFlowService.prepararPasoEnvio();
    // Dejar el estado completo del flujo disponible para el paso Envio.
    const estadoFlujo = this.envioSimpleFlowService.exportarEstadoFlujo();
    // eslint-disable-next-line no-console
    console.log('[ENVIO-SIMPLE][estado hacia paso Envio]', {
      tipoEnvio: estadoFlujo.tipoEnvio,
      documentos: estadoFlujo.documentos.map(estado => ({
        documento: estado.documento,
        formularioDocumento: estado.formularioDocumento.getRawValue()
      }))
    });
    this.envioSimpleFlowService.establecerPaso(2);
    this.router.navigate(['/panel/envio-simple/envio']);
  }

  /**
   * Reglas de flujo para avanzar al paso Envio (D6):
   * 1) al menos un documento;
   * 2) al menos un documento cuyo firmante principal tenga firma completa.
   * Basta con que un documento cumpla la regla 2.
   */
  private validarParaContinuar(): boolean {
    if (!this.envioSimpleFlowService.tieneDocumentos) {
      this.mostrarError('Debe anadir al menos un documento para continuar.');
      return false;
    }
    if (!this.envioSimpleFlowService.algunDocumentoConFirmaPrincipalCompleta()) {
      this.mostrarError(
        'El firmante principal debe tener una firma configurada en al menos un documento.'
      );
      return false;
    }
    if (!this.validarNombresFormularioDuplicados()) {
      return false;
    }
    if (!this.validarFormulariosConCasillas()) {
      return false;
    }
    return true;
  }

  /** Actualiza el estado habilitado de Siguiente sin mostrar snackbars. */
  private actualizarPuedeContinuar(): void {
    this.persistirDocumentoActivoEnStore();
    this.puedeContinuar = this.cumpleReglasFlujo();
  }

  private cumpleReglasFlujo(): boolean {
    if (!this.envioSimpleFlowService.tieneDocumentos) {
      return false;
    }
    if (!this.envioSimpleFlowService.algunDocumentoConFirmaPrincipalCompleta()) {
      return false;
    }
    this.formularioValidacionesDocsService.refrescarErroresNombreFormularioDuplicadoGlobal();
    if (this.formularioValidacionesDocsService.hayErroresNombreFormularioDuplicadoGlobal()) {
      return false;
    }
    return !this.hayFormulariosSinCasillas();
  }

  /**
   * Red de seguridad: nombres de formulario duplicados (mismo firmante o entre
   * firmantes) en el documento activo antes de avanzar al paso Envio.
   */
  private validarNombresFormularioDuplicados(): boolean {
    this.formularioValidacionesDocsService.refrescarErroresNombreFormularioDuplicadoGlobal();
    if (!this.formularioValidacionesDocsService.hayErroresNombreFormularioDuplicadoGlobal()) {
      return true;
    }
    this.formularioValidacionesDocsService.marcarNombresFormularioDuplicadosComoTouched();
    if (this.formularioValidacionesDocsService.hayErroresNombreFormularioDuplicadoEntreFirmantes()) {
      this.mostrarError('Hay errores en los nombres de formulario (duplicados entre firmantes).');
    } else {
      this.mostrarError('Hay errores en los nombres de formulario (duplicados).');
    }
    return false;
  }

  /**
   * Formularios con nombre pero sin ninguna casilla colocada (misma regla que el
   * wizard estandar en paso 8 — panel unificado / Finalizar).
   */
  private validarFormulariosConCasillas(): boolean {
    const mensaje = this.construirMensajeFormulariosIncompletos();
    if (!mensaje) {
      return true;
    }
    this.mostrarError(mensaje);
    return false;
  }

  private hayFormulariosSinCasillas(): boolean {
    this.formularioDocumentoService.pruneBorradoresVaciosSinNombre();
    return this.formularioDocumentoService.getFormulariosIncompletosPaso10().length > 0;
  }

  private construirMensajeFormulariosIncompletos(): string | null {
    this.formularioDocumentoService.pruneBorradoresVaciosSinNombre();
    const incompletos = this.formularioDocumentoService.getFormulariosIncompletosPaso10();
    if (incompletos.length === 0) {
      return null;
    }
    const error: string[] = [];
    const porFirmante = new Map<string, string[]>();
    incompletos.forEach(item => {
      const lista = porFirmante.get(item.firmanteDesc) ?? [];
      lista.push(item.nombre);
      porFirmante.set(item.firmanteDesc, lista);
    });
    porFirmante.forEach((nombres, firmanteDesc) => {
      error.push(`${firmanteDesc} (${nombres.join(', ')})`);
    });
    return `Se tiene que añadir al menos una casilla en el formulario: ${error.join(', ')}.`;
  }

  private mostrarError(mensaje: string): void {
    this.snackBar.open(mensaje, 'Cerrar', { duration: 4000 });
  }

  /** Guarda en el store el FormGroup y el borrador de formularios del activo. */
  private persistirDocumentoActivoEnStore(): void {
    const id = this.documentoActivoId;
    if (!id) {
      return;
    }
    this.envioSimpleFlowService.guardarFormularioDocumento(
      id,
      this.formularioDocumentoService.formularioDocumento
    );
    this.envioSimpleFlowService.guardarBorradoresFormulario(
      id,
      this.formularioDocumentoService.formulariosBorradorFormArray
    );
  }

  /** Actualiza el firmante activo para resaltar overlays en la vista previa. */
  onFirmanteSeleccionado(firmante: TipoFirmante): void {
    this.firmanteSeleccionado = firmante;
  }

  /** Fuerza la recreacion de preview y panel al cambiar el documento activo. */
  trackByDocId(_index: number, id: string | null): string | null {
    return id;
  }

  /** Carga el FormGroup y el PDF del documento activo en el preview reutilizado. */
  private cargarDocumentoActivo(id: string | null): void {
    if (!id) {
      this.persistirBorradoresSalientes(null);
      this.documentoActivoId = null;
      this.pdfSrc = null;
      this.revocarPdfObjectUrl();
      this.formularioChangesSub?.unsubscribe();
      this.actualizarPuedeContinuar();
      return;
    }

    // Persistir FormGroup y borradores del documento saliente antes de conmutar.
    if (this.documentoActivoId && this.documentoActivoId !== id) {
      this.persistirDocumentoActivoEnStore();
    } else {
      this.persistirBorradoresSalientes(id);
    }

    this.formularioDocumentoService.formularioDocumento =
      this.envioSimpleFlowService.getFormularioDocumento(id);
    this.formularioDocumentoService.indexPaso = this.indexPaso;

    // Hidratar la configuracion del generico en su FormGroup antes de recrear
    // preview y panel (que se reconstruyen al fijar documentoActivoId).
    const hidratadoAhora = this.hidratarGenericoSiAplica(id);

    this.sincronizarBorradoresEntrantes(id, hidratadoAhora);

    this.documentoActivoId = id;

    this.actualizarPdfSrc(this.envioSimpleFlowService.documentoActivo);
    this.suscribirCambiosFormulario();
    this.datosConfiguracionDocumentosService.solicitarRefreshOverlaysDocumento();
    this.actualizarPuedeContinuar();
  }

  private suscribirCambiosFormulario(): void {
    this.formularioChangesSub?.unsubscribe();
    const formulario = this.formularioDocumentoService.formularioDocumento;
    if (!formulario) {
      return;
    }
    this.formularioChangesSub = formulario.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.actualizarPuedeContinuar());
  }

  /** Guarda el borrador de formularios del documento que deja de estar activo. */
  private persistirBorradoresSalientes(idEntrante: string | null): void {
    const saliente = this.documentoActivoId;
    if (saliente && saliente !== idEntrante) {
      this.envioSimpleFlowService.guardarBorradoresFormulario(
        saliente,
        this.formularioDocumentoService.formulariosBorradorFormArray
      );
    }
  }

  /**
   * Deja en el servicio el borrador de formularios correcto del documento
   * entrante: reutiliza el guardado (conserva ediciones en curso) o lo deriva
   * del FormGroup. Si el generico se acaba de hidratar, su borrador ya quedo
   * reconstruido desde el FormGroup.
   */
  private sincronizarBorradoresEntrantes(id: string, hidratadoAhora: boolean): void {
    const guardados = this.envioSimpleFlowService.getBorradoresFormulario(id);
    if (guardados) {
      this.formularioDocumentoService.formulariosBorradorFormArray = guardados;
    } else if (!hidratadoAhora) {
      this.formularioDocumentoService.hydrateBorradoresFromFormularios();
    }
    this.envioSimpleFlowService.guardarBorradoresFormulario(
      id,
      this.formularioDocumentoService.formulariosBorradorFormArray
    );
  }

  /**
   * Carga la `configuracion` importada del documento generico en el FormGroup
   * activo (firmantes, firmas, formularios, campos y tipos de contenido), una
   * sola vez por documento para no sobrescribir ediciones del usuario (5b.1).
   * Devuelve `true` si hidrato en esta llamada.
   */
  private hidratarGenericoSiAplica(id: string): boolean {
    if (!this.envioSimpleFlowService.requiereHidratacionGenerico(id)) {
      return false;
    }
    const documento = this.envioSimpleFlowService.documentos.find(doc => doc.id === id);
    if (!documento?.configuracion) {
      return false;
    }
    this.formularioDocumentoService.hydrateOverlaysFromDocumento(documento.configuracion);
    this.envioSimpleFlowService.guardarFormularioDocumento(
      id,
      this.formularioDocumentoService.formularioDocumento
    );
    this.envioSimpleFlowService.marcarDocumentoHidratado(id);
    // Re-sincronizar roster tras hidratar: la union puede crecer y debe
    // propagarse a todos los documentos sin pisar la config ya importada.
    this.envioSimpleFlowService.sincronizarRosterDesdeDocumentos();
    this.formularioDocumentoService.formularioDocumento =
      this.envioSimpleFlowService.getFormularioDocumento(id);
    this.datosConfiguracionDocumentosService.solicitarRefreshOverlaysDocumento();
    return true;
  }

  private actualizarPdfSrc(documento: DocumentoEnvioSimple | null): void {
    this.revocarPdfObjectUrl();

    const binario: Blob | undefined = documento?.archivo ?? documento?.pdfBlob;
    if (!binario) {
      this.pdfSrc = null;
      return;
    }

    this.pdfObjectUrl = URL.createObjectURL(binario);
    this.pdfSrc = this.pdfObjectUrl;
  }

  private revocarPdfObjectUrl(): void {
    if (this.pdfObjectUrl) {
      URL.revokeObjectURL(this.pdfObjectUrl);
      this.pdfObjectUrl = null;
    }
  }
}
